import { z } from 'zod';

export const UserIdSchema = z.guid().brand<'UserId'>();

export type UserId = z.infer<typeof UserIdSchema>;

export const UserSchema = z.object({
  id: UserIdSchema,
  name: z.string(),
  email: z.email(),
});

export type User = z.infer<typeof UserSchema>;

/**
 * The org-membership roles owned by accounts-api. Kept here (shared)
 * so any service that consumes accounts-api's internal endpoints can
 * type the response without redefining the enum.
 */
export const OrgRoleSchema = z.enum(['owner', 'admin', 'member', 'guest']);
export type OrgRole = z.infer<typeof OrgRoleSchema>;

/**
 * App-user id as exposed by accounts-api: the MongoDB ObjectId hex of the
 * record in the `users` collection (accounts-api's database). All
 * downstream services receive this opaque 24-char string and never the
 * underlying ObjectId.
 */
export const AppUserIdSchema = z
  .string()
  .regex(/^[a-f\d]{24}$/i, 'must be a 24-char hex appUserId');

/**
 * A user enriched with their role in a given organization. This is the
 * canonical shape served by accounts-api's `GET /internal/orgs/:orgId/users`
 * endpoint — kept in shared domain so service-to-service consumers
 * (kanban-api, future APIs) validate the same wire contract.
 */
export const OrgUserSchema = z.object({
  userId: AppUserIdSchema,
  cognitoSub: z.string().min(1),
  name: z.string(),
  email: z.string(),
  orgRole: OrgRoleSchema,
});

export type OrgUser = z.infer<typeof OrgUserSchema>;

/**
 * Resposta de `GET /internal/users/by-cognito-sub/:sub` do accounts-api, na
 * parte que a autenticação compartilhada (api-kit) lê antes de montar
 * `req.user`. Espelha o `UserRecordSchema` de accounts-api; `.loose()` porque
 * o endpoint também devolve campos de perfil (`displayName`, `phone`,
 * `whatsapp`) que não são contrato para os consumidores.
 */
export const AccountsInternalUserSchema = z
  .object({
    id: z.string().min(1),
    cognitoSub: z.string().min(1),
    email: z.string(),
    name: z.string(),
    isStaff: z.boolean(),
  })
  .loose();

export type AccountsInternalUser = z.infer<typeof AccountsInternalUserSchema>;
